<template>
  <nav class="flex flex-row relative" ref="nav">
    <div
      v-for="(definition, index) in STATISTIC_DEFINITIONS"
      :key="index"
      class="p-2 text-center text-sm relative cursor-pointer"
      :class="selectedStat?.name == definition.name ? 'text-text-primary' : 'text-text-secondary'"
      @click="selectStat(definition)"
    >
      <img :src="definition.imageSrc" class="h-4 w-4 inline" :class="selectedStat?.name == definition.name ? 'brightness-200' : ''">
      <span :class="selectedStat?.name == definition.name ? 'hidden sm:inline ml-1' : 'hidden xl:inline ml-1'">{{ $t(`training.statistic_charts.${definition.key}`) }}</span>
    </div>
    <div class="sliding-border" :style="slidingBorderStyle" />
    <div class="ml-auto">
      <slot />
    </div>
  </nav>

  <LineChart class="mt-2 w-full max-h-[130px]" :key="selectedStat.name" :options="chartOptions" :chartData="getChartData()" />
</template>

<script setup>
import moment from 'moment';
import {
  ref, computed, onMounted, nextTick, defineEmits,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useI18n } from 'vue-i18n';
import { LineChart } from 'vue-chart-3';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { formatData, formatStatStringData } from '../../utils/formatters.js';
import { coachStatKey, fetchCoachProfile } from '../../utils/coach_pipeline.js';

const props = defineProps({
  statistics: {
    type: Object,
    required: true,
  },
  singleSession: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['selectedMetricChange']);

Chart.register(...registerables);
Chart.register('chartjs-adapter-date-fns');

const STATISTIC_DEFINITIONS = [
  {
    key: 'accuracy', // NOTE(ssaunier): key are used in Task.vue to emphesize the selected metric
    name: 'Accuracy',
    userStatsKey: null,
    getData: (stat) => Math.min(1, (stat.total_headshots + stat.total_bodyshots) / stat.total_bullets_fired) * 100,
    unit: 'percentage',
    better: 'max',
    imageSrc: new URL('../../assets/icons/accuracy.svg', import.meta.url).href,
  },
  {
    key: 'counter_strafing',
    name: 'Counter-strafing',
    userStatsKey: null,
    getData: (stat) => stat.average_counter_strafe,
    unit: 'percentage',
    better: 'max',
    imageSrc: new URL('../../assets/icons/counter-strafing.svg', import.meta.url).href,
  },
  {
    key: 'crosshair_placement',
    name: 'Crosshair placement',
    userStatsKey: 'crosshair_placement',
    getData: (stat) => stat.average_crosshair_drift,
    unit: 'degrees',
    better: 'min',
    imageSrc: new URL('../../assets/icons/crosshair.svg', import.meta.url).href,
  },
  {
    key: 'headshot_percentage',
    name: 'Headshot %',
    userStatsKey: 'headshot_percentage',
    getData: (stat) => stat.total_headshots / (stat.total_headshots + stat.total_bodyshots) * 100,
    unit: 'percentage',
    better: 'max',
    imageSrc: new URL('../../assets/icons/headshot_percentage.svg', import.meta.url).href,
  },
  {
    key: 'reaction_time',
    name: 'Reaction time',
    userStatsKey: null,
    getData: (stat) => stat.average_reaction_time,
    unit: 'seconds',
    better: 'min',
    imageSrc: new URL('../../assets/icons/reaction_time.svg', import.meta.url).href,
  },
  {
    key: 'time_to_kill',
    name: 'Time to kill',
    userStatsKey: 'time_to_kill',
    getData: (stat) => stat.average_time_to_kill,
    unit: 'seconds',
    better: 'min',
    imageSrc: new URL('../../assets/icons/time_to_kill.svg', import.meta.url).href,
  },
];

const { t } = useI18n();
const auth = useAuth();
const selectedStat = ref(STATISTIC_DEFINITIONS[0]);
const showCharts = ref(false);
const nav = ref(null);
const slidingBorderStyle = ref({});
const userStats = ref({});

const chartOptions = computed(() => ({
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      filter(context) {
        return context.datasetIndex === 0; // Tooltip only on task stats, not on cohort baseline stats
      },
      callbacks: {
        title: (items) => {
          const task = items[0]?.raw?.task;
          if (task) {
            let start = '';
            if (task.completion?.started_at) {
              start = `${moment(task.completion.started_at).format('h:mm A')} - `;
            }
            return t('training.statistic_charts.tooltip_title', { start, task: task.name ?? task.mod.friendly_name, map: task.cs_map.friendly_name });
          } if (items[0]?.raw) {
            // return moment(task.completion.started_at).format("h:mm A")
            return moment(items[0].raw.x).calendar(null, {
              sameDay: '[Today at] h:mm A', lastDay: '[Yesterday at] h:mm A', lastWeek: 'MMMM Do [at] h:mm A', sameElse: 'MMMM Do [at] h:mm A',
            });
          }
        },
        label(item) {
          let rawValue = item.raw.y;
          if (selectedStat.value.unit === 'percentage') {
            rawValue /= 100;
          }

          return formatStatStringData(rawValue, selectedStat.value.unit);
        },
      },
    },
  },
  elements: {
    point: {
      radius: 3,
      hoverRadius: 6,
    },
  },
  scales: {
    x: {
      type: 'timeseries',
      display: true,
      ticks: {
        callback: (value, index) => {
          if (props.singleSession) {
            return moment(value).format('HH:mm');
          } if (index % Math.ceil((props.statistics.length / 10)) === 0) {
            return moment(value).format('MMM D, HH:mm');
          }
        },
      },
    },
    y: {
      suggestedMin: 0,
      suggestedMax: selectedStat.value?.unit === 'percentage' ? 100 : null,
      grid: {
        color: '#37485D',
        borderColor: 'transparent',
      },
      title: {
        display: !!selectedStat.value,
        text: selectedStat.value?.better === 'max' ? '→' : '←',
        padding: 0,
      },
      ticks: {
        color: '#9CADC4',
        stepSize: (() => {
          switch (selectedStat.value?.unit) {
            case 'percentage':
              return 25;
            case 'seconds':
              return 0.25;
            default:
              return 1;
          }
        })(),
        callback: (value, index, ticks) => {
          switch (selectedStat.value.unit) {
            case 'percentage':
              return `${value}%`;
            case 'degrees':
              return `${value}°`;
            case 'seconds':
              return `${value}s`;
            default:
              return value;
          }
        },
      },
    },
  },
}));

function getChartData() {
  const datasets = [];
  datasets.push({
    data: props.statistics.map((stat) => ({
      x: new Date(stat.created_at),
      y: selectedStat.value.getData(stat),
      task: stat.task,
    })).filter((datum) => !Number.isNaN(datum.y)),
    color: 'white',
    fill: true,
    backgroundColor: '#6B70D115',
    borderColor: '#6B70D1',
    tension: 0.1,
    spanGaps: true,
  });

  if (selectedStat.value.userStatsKey) {
    const targetStat = userStats.value[coachStatKey(selectedStat.value.userStatsKey)];
    // A null average means there is no peer bracket; no line then.
    if (targetStat && targetStat.rating_range_average_value != null) {
      const targetValue = formatData(targetStat.rating_range_average_value, targetStat.unit_type);

      datasets.push({
        data: props.statistics.map((stat) => ({
          x: new Date(stat.created_at),
          y: targetValue,
        })),
        fill: false,
        borderColor: '#C44CB6',
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 1,
        hoverRadius: 0,
        hoverBorderWidth: 0,
      });
    }
  }

  return {
    datasets,
  };
}

function selectStat(stat) {
  selectedStat.value = stat;
  computeSlidingBorderStyle();
  emit('selectedMetricChange', stat);
}

const computeSlidingBorderStyle = async () => {
  await nextTick();
  if (!nav.value) return;

  const index = STATISTIC_DEFINITIONS.findIndex((stat) => stat.name === selectedStat.value?.name);
  if (index === -1) return; // Handle case where stat is not found

  const left = Array.from({ length: index }, (_, i) => nav.value.children[i].offsetWidth)
    .reduce((sum, width) => sum + width, 0);
  const width = nav.value.children[index].offsetWidth;

  slidingBorderStyle.value = {
    left: `${left}px`,
    width: `${width}px`,
  };
};

function getTargetData() {
  fetchCoachProfile(auth.user())
    .then(({ stats }) => {
      userStats.value = stats;
    })
    .catch((error) => {
      // If matches are not enabled, silently fail, we just won't display the target line in the chart
      console.error('Could not load User Stats from Coach API', error);
    });
}

onMounted(() => {
  selectStat(STATISTIC_DEFINITIONS[0]);
  window.addEventListener('resize', computeSlidingBorderStyle);
  if (props.statistics?.length > 0) {
    getTargetData();
  }
});

</script>

<style scoped>
.sliding-border {
  position: absolute;
  height: 2px;
  bottom: 0;
  /* `left` & `width` is JS computed in `computeSlidingBorderStyle()` */
  background-color: theme("colors.primary.500");
  transition: all 0.3s ease-in-out;
}
</style>
